/** Module entitlement keys. `DATA_MODEL.md` section 3. */
export const MODULE_KEYS = ['dash', 'tick', 'ai', 'mod', 'roi', 'sec'] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

/** Module names and the one-line descriptions from spec 07, entitlements card. */
export const MODULES: Record<ModuleKey, { label: string; description: string }> = {
  dash: { label: 'Dashboards', description: 'Cost, security and operations views' },
  tick: { label: 'Ticketing Platform', description: 'Queue, ticket detail, automation rules' },
  ai: { label: 'AI Workspace', description: 'Agents catalog and AI governance' },
  mod: { label: 'Modernization Space', description: 'Workloads, assessments, business cases, waves' },
  roi: { label: 'ROI Analysis', description: 'TCO and business value calculators' },
  sec: { label: 'Security and Compliance', description: 'Posture, findings, evidence packs' },
};

export type Entitlements = Record<ModuleKey, boolean>;

export const PLANS = ['Essential', 'Advanced', 'Premier'] as const;
export type Plan = (typeof PLANS)[number];

export const TENANT_STATUSES = ['Active', 'Onboarding', 'Suspended'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

/** AWS Partner Central industry verticals, the same list Raptor Labs asks for. */
export const INDUSTRIES = [
  'Aerospace',
  'Automotive',
  'Computers and Electronics',
  'Consumer Goods',
  'Education',
  'Energy - Oil and Gas',
  'Energy - Power and Utilities',
  'Financial Services',
  'Gaming',
  'Government',
  'Healthcare',
  'Hospitality',
  'Life Sciences',
  'Manufacturing',
  'Marketing and Advertising',
  'Media and Entertainment',
  'Mining',
  'Non-Profit Organization',
  'Real Estate and Construction',
  'Retail',
  'Software and Internet',
  'Telecommunications',
  'Transportation and Logistics',
  'Travel',
  'Wholesale and Distribution',
  'Other',
] as const;
export type Industry = (typeof INDUSTRIES)[number];


/** Per-tenant single sign-on, configured by DinoCloud. The secret lives only in Cognito. */
export interface TenantSso {
  type: 'saml' | 'oidc';
  /** Cognito identity provider name, e.g. `tenant-banco-delta`. */
  providerName: string;
  /** What the sign-in button says, e.g. "Okta" or "Microsoft Entra ID". */
  label: string;
  metadataUrl?: string;
  issuer?: string;
  clientId?: string;
  /** When true, people at this tenant cannot sign in with a password. */
  enforce: boolean;
  configuredAt: string;
  configuredBy: string;
}

/** Quarterly confirmation that the people list is right. */
export interface AccessReviewRecord {
  lastCompletedAt?: string;
  completedBy?: string;
  lastRemindedAt?: string;
}

/**
 * A client organization. Authored fields only — seat usage, module count and the billing
 * label are derived in `derived/tenant.ts` and never stored.
 */
export interface Tenant {
  id: string;
  name: string;
  industry: Industry;
  plan: Plan;
  status: TenantStatus;
  /** Client since, month and year, e.g. "March 2023". */
  since: string;
  /** Customer success manager, a DinoCloud staff name. */
  csm: string;
  /** Integer USD per month. 0 when suspended or not yet billed. */
  mrr: number;
  /** Contracted seat limit. */
  seats: number;
  /** Linked AWS accounts, "name (0000 0000 0000)". */
  accounts: string[];
  /** Data residency. */
  region: string;
  /** "Okta SAML", "Microsoft Entra ID", "Not configured". */
  sso: string;
  /** "Required for every role", "Required for admins". */
  mfa: string;
  modules: Entitlements;
  /** Corporate email domains whose users may be invited and may sign in. Lowercase. */
  allowedDomains: string[];
  /** ISO timestamp. */
  createdAt: string;
  /** Original MRR kept while suspended so reactivation restores it. */
  mrrBeforeSuspension?: number;
  ssoProvider?: TenantSso;
  accessReview?: AccessReviewRecord;
}
